import { cn } from "@/lib/utils";
import type { Character, Room } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { KeyRound, MessagesSquare, Plus, UsersRound, X } from "lucide-react";
import { CharacterAvatar } from "@/CharacterAvatar";

export function Sidebar({
  rooms,
  activeRoom,
  view,
  activeCast,
  onSelectRoom,
  onNewRoom,
  onToggleChars,
  onOpenProviderSettings,
  open,
  onClose
}: {
  rooms: Room[];
  activeRoom: number | null;
  view: "chat" | "chars";
  activeCast: Character[];
  onSelectRoom: (id: number) => void;
  onNewRoom: () => void;
  onToggleChars: () => void;
  onOpenProviderSettings: () => void;
  open: boolean;
  onClose: () => void;
}) {
  const row = "flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm transition-colors";
  const active =
    "bg-primary/20 text-foreground";
  const idle = "text-muted-foreground hover:bg-muted/60";

  return (
    <>
      {open && <button aria-label="Close navigation" className="fixed inset-0 z-30 bg-black/60 md:hidden" onClick={onClose} />}
      <aside className={cn(
        "fixed inset-y-0 left-0 z-40 flex w-[min(82vw,18rem)] flex-col border-r border-border bg-card p-3 shadow-2xl transition-transform duration-200 md:static md:z-auto md:w-72 md:translate-x-0 md:shadow-none",
        open ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="mb-4 flex items-center justify-between px-2 md:mb-5">
          <div>
            <div className="text-sm font-semibold tracking-tight">k6waifus</div>
            <div className="text-xs text-muted-foreground">Your roleplay rooms</div>
          </div>
          <Button aria-label="Close navigation" variant="ghost" size="icon" className="md:hidden" onClick={onClose}>
            <X />
          </Button>
        </div>
      <div className="mb-2 px-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Rooms</div>
      <div className="space-y-1">
      {rooms.map((r) => (
        <button
          key={r.id}
          onClick={() => {
            onSelectRoom(r.id);
            onClose();
          }}
          className={cn(row, activeRoom === r.id && view === "chat" ? active : idle)}
        >
          <MessagesSquare size={17} />
          <span className="truncate">{r.name}</span>
        </button>
      ))}
      </div>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => {
          onNewRoom();
          onClose();
        }}
        className="mt-3 w-full justify-start"
      >
        <Plus /> New room
      </Button>

      <div className="mb-2 mt-7 px-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Manage</div>
      <button
        onClick={() => {
          onToggleChars();
          onClose();
        }}
        className={cn(row, view === "chars" ? active : idle)}
      >
        <UsersRound size={17} />
        <span>Characters</span>
      </button>
      <button
        onClick={() => {
          onOpenProviderSettings();
          onClose();
        }}
        className={cn(row, idle)}
      >
        <KeyRound size={17} />
        <span>Provider settings</span>
      </button>

      {view === "chat" && activeCast.length > 0 && (
        <div className="mb-2 mt-7 px-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Cast</div>
      )}
      {view === "chat" &&
        activeCast.map((c) => (
          <div key={c.id} className="flex items-center gap-2 px-2 py-1 text-xs">
            <CharacterAvatar character={c} className="h-6 w-6 text-xs" />
            <span style={{ color: c.color }}>{c.name}</span>
          </div>
        ))}
      </aside>
    </>
  );
}